import React, { useState, useEffect } from 'react';
import styles from './CreateUser.module.css';
import { toast } from '../../../lib/toast';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';
import { RegistrationSource } from '../../../enums/registrationSource';
import { useUsersStore } from '../../../store/usersStore';
import { useAuthStore } from '../../../store/authStore';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { User, Mail, Lock } from 'lucide-react';
import { validatePassword } from '../../../utils/security';

interface CreateUserProps {
    showCreateUser: boolean;
    setShowCreateUser: (show: boolean) => void;
}


export const CreateUser: React.FC<CreateUserProps> = ({ showCreateUser, setShowCreateUser }) => {
    const { createUser: createUserInStore, } = useUsersStore()
    const currentUser = useAuthStore(state => state.user)
    const [newUserForm, setNewUserForm] = useState({
        email: '',
        first_name: '',
        last_name: '',
        password: '' as string | null,
        confirm_password: '' as string | null,
        role: UserRole.Viewer,
        registrationSource: RegistrationSource.Local // Default value
    })
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    const updateField = (field: string, value: string) => {
        setNewUserForm(prev => ({ ...prev, [field]: value }))
        setFieldErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev))
    }

    // Clear password fields when switching to IDP
    useEffect(() => {
        if (newUserForm.registrationSource === RegistrationSource.Idp) {
            setNewUserForm(prev => ({
                ...prev,
                password: '',
                confirm_password: ''
            }))
        }
    }, [newUserForm.registrationSource])

    const createUser = async () => {
        const withoutPassword = newUserForm.registrationSource === RegistrationSource.Idp;

        const errors: Record<string, string> = {}

        if (!newUserForm.first_name.trim()) {
            errors.first_name = 'First name is required'
        }

        if (!newUserForm.last_name.trim()) {
            errors.last_name = 'Last name is required'
        }

        if (!newUserForm.email.trim()) {
            errors.email = 'Email is required'
        }

        // Validate password if required
        if (!withoutPassword) {
            if (!newUserForm.password?.trim()) {
                errors.password = 'Password is required for local registration'
            } else {
                const passwordValidation = validatePassword(newUserForm.password)
                if (!passwordValidation.valid) {
                    errors.password = passwordValidation.error || 'Invalid password'
                }
            }

            if (!newUserForm.confirm_password?.trim()) {
                errors.confirm_password = 'Please confirm your password'
            } else if (newUserForm.password !== newUserForm.confirm_password) {
                errors.confirm_password = 'Passwords do not match'
            }
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            toast.error('Please fix the highlighted fields')
            return
        }

        setFieldErrors({})

        if (!newUserForm.registrationSource) {
            toast.error('Please select a registration source')
            return
        }

        let userPayload: any = {
            email: newUserForm.email.trim(),
            first_name: newUserForm.first_name.trim(),
            last_name: newUserForm.last_name.trim(),
            role: newUserForm.role,
            registrationSource: newUserForm.registrationSource || RegistrationSource.Idp, // Default to Local if somehow not set
        };

        // Only include password fields for local registration
        if (!withoutPassword && newUserForm.password) {
            userPayload.password = newUserForm.password;
        }

        try {
            await createUserInStore(userPayload)
            setShowCreateUser(false)
            setNewUserForm({
                email: '',
                first_name: '',
                last_name: '',
                password: '',
                confirm_password: '',
                role: UserRole.Viewer,
                registrationSource: RegistrationSource.Local // Default reset value
            })
            setFieldErrors({})
            toast.success('User created successfully!')
        } catch (error: any) {
            toast.error(`Failed to create user: ${error.message}`)
        }
    }

    return (
        <div>
            {showCreateUser && (
                <div className={styles.createForm}>
                    <h3>Create New User</h3>
                    <div className={styles.formGrid}>
                        <SecureInput
                            fieldType="name"
                            label="First Name *"
                            fieldName="First name"
                            icon={<User size={16} />}
                            value={newUserForm.first_name}
                            onChange={(value) => updateField('first_name', value)}
                            placeholder="First name"
                            required
                            externalError={fieldErrors.first_name}
                            className={styles.formField}
                        />
                        <SecureInput
                            fieldType="name"
                            label="Last Name *"
                            fieldName="Last name"
                            icon={<User size={16} />}
                            value={newUserForm.last_name}
                            onChange={(value) => updateField('last_name', value)}
                            placeholder="Last name"
                            required
                            externalError={fieldErrors.last_name}
                            className={styles.formField}
                        />
                        <SecureInput
                            fieldType="email"
                            label="Email Address *"
                            icon={<Mail size={16} />}
                            value={newUserForm.email}
                            onChange={(value) => updateField('email', value)}
                            placeholder="Email address"
                            required
                            externalError={fieldErrors.email}
                            autoComplete="new-email"
                            className={styles.formField}
                        />
                        <SecureInput
                            fieldType="password"
                            label="Password"
                            icon={<Lock size={16} />}
                            value={newUserForm.password || ''}
                            onChange={(value) => updateField('password', value)}
                            placeholder="Password"
                            externalError={fieldErrors.password}
                            disabled={newUserForm.registrationSource === RegistrationSource.Idp}
                            autoComplete="new-password"
                            className={styles.formField}
                            required={newUserForm.registrationSource === RegistrationSource.Local}
                        />
                        <SecureInput
                            fieldType="confirmPassword"
                            label="Confirm Password"
                            icon={<Lock size={16} />}
                            value={newUserForm.confirm_password || ''}
                            onChange={(value) => updateField('confirm_password', value)}
                            placeholder="Confirm password"
                            externalError={fieldErrors.confirm_password}
                            matchValue={newUserForm.password || ''}
                            disabled={newUserForm.registrationSource === RegistrationSource.Idp}
                            autoComplete="new-password"
                            preventPaste
                            className={styles.formField}
                            required={newUserForm.registrationSource === RegistrationSource.Local}
                        />
                        <div className={styles.formField}>
                            <label className={styles.fieldLabel}>Role *</label>
                            <select
                                value={newUserForm.role}
                                onChange={(e) => setNewUserForm({ ...newUserForm, role: Number(e.target.value) })}
                                className={styles.formInput}
                            >
                                <option value={UserRole.Viewer}>Viewer</option>
                                <option value={UserRole.ToolManager}>Tool Manager</option>
                                {hasMinRole(currentUser?.role?.id, UserRole.Admin) && (
                                    <option value={UserRole.WorkzoneManager}>Workzone Manager</option>
                                )}
                                {hasMinRole(currentUser?.role?.id, UserRole.Admin) && (
                                    <option value={UserRole.PromptEditor}>Prompt Editor</option>
                                )}
                                {hasMinRole(currentUser?.role?.id, UserRole.Admin) && (
                                    <option value={UserRole.Admin}>Admin</option>
                                )}
                                {currentUser?.role?.id === UserRole.SuperAdmin && (
                                    <option value={UserRole.SuperAdmin}>Super Admin</option>
                                )}
                            </select>
                        </div>
                        {hasMinRole(currentUser?.role?.id, UserRole.SuperAdmin) && (
                            <div className={styles.formField}>
                                <label className={styles.fieldLabel}>Registration Source *</label>
                                <select
                                    value={newUserForm.registrationSource}
                                    onChange={(e) => {
                                        const newSource = e.target.value as RegistrationSource;
                                        setNewUserForm({
                                            ...newUserForm,
                                            registrationSource: newSource,
                                            // Clear password fields when switching to IDP
                                            password: newSource === RegistrationSource.Idp ? '' : newUserForm.password,
                                            confirm_password: newSource === RegistrationSource.Idp ? '' : newUserForm.confirm_password
                                        });
                                    }}
                                    className={styles.formInput}
                                >
                                    <option value={RegistrationSource.Idp}>IDP</option>
                                    <option value={RegistrationSource.Local}>Local</option>
                                </select>
                            </div>)}
                    </div>
                    <div className={styles.formActions}>

                        <button onClick={() => setShowCreateUser(false)} className={styles.cancelBtn}>
                            Cancel
                        </button>
                        <button onClick={createUser} className={styles.primaryBtn}>
                            Create User
                        </button>
                    </div>
                </div>
            )}</div>
    );

};

export default CreateUser;